import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Save, Bot, Hand, Volume2, Languages } from 'lucide-react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pages = [
  { key: 'home', label: 'Home', description: 'Main landing page with tree animation' },
  { key: 'about', label: 'About', description: 'Mission, vision, and leadership' },
  { key: 'sermons', label: 'Sermons', description: 'Video sermons page' },
  { key: 'podcast', label: 'Podcast', description: 'Spotify podcast episodes' },
  { key: 'programs', label: 'Programs', description: 'Ministry programs and pantry locations' },
  { key: 'events', label: 'Events', description: 'Upcoming events and activities' },
  { key: 'blog', label: 'Blog/News', description: 'News and blog posts' },
  { key: 'prayers', label: 'Prayer Requests', description: 'Prayer submission and wall' },
  { key: 'qa', label: 'Q&A', description: 'Questions and answers' },
  { key: 'partners', label: 'Partners', description: 'Partner organizations directory' },
  { key: 'donate', label: 'Donate', description: 'Donation page' },
  { key: 'volunteer', label: 'Volunteer', description: 'Volunteer sign-up' },
  { key: 'contact', label: 'Contact', description: 'Contact form and info' },
  { key: 'smallGroups', label: 'Small Groups', description: 'Small groups finder' },
  { key: 'businessTeaching', label: 'Business Classes', description: 'Business teaching signup' },
  { key: 'localBusinesses', label: 'Local Businesses', description: 'Community business directory' },
];

const bots = [
  { 
    key: 'translatorBot', 
    label: 'Translator Bot', 
    description: 'AI-powered text translation and text-to-speech assistant',
    icon: Languages,
    color: 'burgundy'
  },
  { 
    key: 'signLanguageBot', 
    label: 'Sign Language Avatar', 
    description: 'Animated ASL/Libras sign language interpreter',
    icon: Hand,
    color: 'purple'
  },
];

export default function AdminPageVisibility() {
  const [visibility, setVisibility] = useState({});
  const [botSettings, setBotSettings] = useState({
    translatorBot: true,
    signLanguageBot: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [visRes, botRes] = await Promise.all([
        axios.get(`${API}/page-visibility`, { withCredentials: true }),
        axios.get(`${API}/bot-settings`, { withCredentials: true }).catch(() => ({ data: { translatorBot: true, signLanguageBot: true } }))
      ]);
      setVisibility(visRes.data);
      setBotSettings(botRes.data || { translatorBot: true, signLanguageBot: true });
    } catch (error) {
      const defaults = {};
      pages.forEach(p => defaults[p.key] = true);
      setVisibility(defaults);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setVisibility(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleBotToggle = (key) => {
    setBotSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        axios.put(`${API}/page-visibility`, visibility, { withCredentials: true }),
        axios.put(`${API}/bot-settings`, botSettings, { withCredentials: true })
      ]);
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-pages">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl text-charcoal">Site Controls</h1>
          <p className="text-charcoal/60 mt-1">Manage page visibility and accessibility features</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-burgundy hover:bg-purple text-white"
          data-testid="save-visibility"
        >
          <Save size={18} className="mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Bot Controls Section */}
      <div className="mb-8">
        <h2 className="font-serif text-xl text-charcoal mb-4 flex items-center gap-2">
          <Bot size={24} className="text-burgundy" />
          Accessibility Bots
        </h2>
        <div className="bg-gradient-to-r from-purple/5 to-burgundy/5 rounded-2xl border border-purple/20 overflow-hidden">
          {bots.map((bot, i) => {
            const Icon = bot.icon;
            return (
              <div 
                key={bot.key}
                className={`flex items-center justify-between p-5 ${i > 0 ? 'border-t border-purple/10' : ''}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${botSettings[bot.key] ? `bg-${bot.color}/20` : 'bg-charcoal/5'}`}>
                    <Icon size={24} className={botSettings[bot.key] ? `text-${bot.color}` : 'text-charcoal/30'} />
                  </div>
                  <div>
                    <p className="font-medium text-charcoal text-lg">{bot.label}</p>
                    <p className="text-sm text-charcoal/50">{bot.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    botSettings[bot.key] 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-600'
                  }`}>
                    {botSettings[bot.key] ? 'ON' : 'OFF'}
                  </span>
                  <Switch
                    checked={botSettings[bot.key] ?? true}
                    onCheckedChange={() => handleBotToggle(bot.key)}
                    data-testid={`toggle-${bot.key}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-sm text-charcoal/50 mt-2 ml-1">
          💡 These accessibility features help visitors with different needs access your content.
        </p>
      </div>

      {/* Page Visibility Section */}
      <div>
        <h2 className="font-serif text-xl text-charcoal mb-4 flex items-center gap-2">
          <Eye size={24} className="text-olive" />
          Page Visibility
        </h2>
        <div className="bg-white rounded-2xl border border-gold/10 overflow-hidden">
          {pages.map((page, i) => (
            <div 
              key={page.key}
              className={`flex items-center justify-between p-4 ${i > 0 ? 'border-t border-cream' : ''}`}
            >
              <div className="flex items-center gap-4">
                <div className={`p-2 rounded-lg ${visibility[page.key] ? 'bg-olive/10' : 'bg-charcoal/5'}`}>
                  {visibility[page.key] ? (
                    <Eye size={20} className="text-olive" />
                  ) : (
                    <EyeOff size={20} className="text-charcoal/30" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-charcoal">{page.label}</p>
                  <p className="text-sm text-charcoal/50">{page.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                  visibility[page.key] 
                    ? 'bg-olive/10 text-olive' 
                    : 'bg-charcoal/10 text-charcoal/50'
                }`}>
                  {visibility[page.key] ? 'Visible' : 'Hidden'}
                </span>
                <Switch
                  checked={visibility[page.key] ?? true}
                  onCheckedChange={() => handleToggle(page.key)}
                  data-testid={`toggle-${page.key}`}
                />
              </div>
            </div>
          ))}
        </div>

        <p className="text-sm text-charcoal/50 mt-4">
          Note: Hidden pages will not appear in the navigation menu, but may still be accessible via direct URL.
        </p>
      </div>
    </div>
  );
}
